<template>
  <div class="test-page">
    <div class="test-header">
      <div class="test-badge">TEST</div>
      <div class="test-title">交易支付调试</div>
    </div>

    <div class="mode-switch">
      <div class="mode-item" :class="{ active: mode === 'manual' }" @click="switchMode('manual')">手动填写参数</div>
      <div class="mode-item" :class="{ active: mode === 'direct' }" @click="switchMode('direct')">商户号直接下单</div>
    </div>

    <div class="card">
      <div class="card-title">当前环境</div>
      <div class="info-row">
        <span class="info-label">检测结果</span>
        <span class="info-value" :class="envDetected ? 'green' : 'orange'">{{ envText }}</span>
      </div>
      <div class="info-row">
        <span class="info-label">User-Agent</span>
        <span class="info-value small breakable">{{ ua }}</span>
      </div>
    </div>

    <div class="tabs">
      <div
        v-for="t in payTypes"
        :key="t.value"
        class="tab-item"
        :class="{ active: activePayType === t.value }"
        :style="activePayType === t.value ? ('background:' + t.color + ';border-color:' + t.color + ';') : ''"
        @click="switchType(t.value)"
      >
        {{ t.label }}
      </div>
    </div>

    <div class="card" v-if="mode === 'manual'">
      <div class="card-title">支付参数（JSON）</div>
      <textarea
        class="params-textarea"
        v-model="paramsText[activePayType]"
        :placeholder="currentPlaceholder"
        rows="7"
      ></textarea>
      <div class="params-actions">
        <button class="mini-btn" @click="fillSample">填充示例参数</button>
        <button class="mini-btn" @click="formatParams">格式化</button>
        <button class="mini-btn mini-btn-clear" @click="clearParams">清空</button>
      </div>
    </div>

    <div class="card" v-if="mode === 'direct'">
      <div class="card-title">商户号直接下单（调用 /api/cashier/direct-pay）</div>

      <div v-if="!directWayCode" class="direct-unsupported">
        当前支付方式「{{ currentTypeLabel }}」暂不支持直连下单，该接口仅支持微信 / 支付宝。
      </div>

      <template v-else>
        <div class="form-row">
          <span class="form-label">商户号<span class="required">*</span></span>
          <input class="form-input" v-model="directForm.mchNo" placeholder="mchNo" />
        </div>
        <div class="form-row">
          <span class="form-label">应用ID</span>
          <span class="form-static">{{ FIXED_APP_ID }}<span class="fixed-tag">写死</span></span>
        </div>
        <div class="form-row" v-if="activePayType === 'wxpay'">
          <span class="form-label">公众号AppId</span>
          <input class="form-input" v-model="directForm.subAppId" placeholder="subAppId（获取openid所用的公众号AppId，选填）" />
        </div>
        <div class="form-row">
          <span class="form-label">渠道用户标识</span>
          <span class="form-static">{{ FIXED_CHANNEL_USER }}<span class="fixed-tag">写死</span></span>
        </div>
        <div class="form-row">
          <span class="form-label">金额（元）<span class="required">*</span></span>
          <input class="form-input" v-model="directForm.amountYuan" placeholder="1.00" />
        </div>
        <div class="form-row">
          <span class="form-label">订单标题</span>
          <input class="form-input" v-model="directForm.subject" placeholder="商户收款" />
        </div>
        <div class="form-row">
          <span class="form-label">备注</span>
          <input class="form-input" v-model="directForm.remark" placeholder="选填" />
        </div>
        <div class="form-row">
          <span class="form-label">支付接口</span>
          <span class="form-static">{{ FIXED_IF_CODE }}<span class="fixed-tag">写死</span></span>
        </div>
        <div class="form-row">
          <span class="form-label">wayCode</span>
          <span class="form-static">{{ directWayCode }}</span>
        </div>
      </template>
    </div>

    <div class="pay-btn-wrap">
      <button
        v-if="mode === 'manual'"
        class="pay-btn"
        :style="'background:' + currentColor + ';'"
        @click="triggerPay"
        :disabled="paying"
      >
        {{ paying ? '发起中...' : '立即发起测试支付' }}
      </button>
      <button
        v-else
        class="pay-btn"
        :style="'background:' + currentColor + ';'"
        @click="submitDirectPay"
        :disabled="paying || !directWayCode"
      >
        {{ paying ? '下单中...' : '调用 direct-pay 并发起支付' }}
      </button>
    </div>

    <div v-if="resultMsg" class="result-box" :class="resultSuccess ? 'success' : 'fail'">
      {{ resultMsg }}
    </div>

    <div class="log-card" v-if="logs.length > 0">
      <div class="card-title">调试日志</div>
      <div v-for="(log, i) in logs" :key="i" class="log-item">{{ log }}</div>
    </div>
  </div>
</template>

<script>
import { directPay } from '@/api/api'

const STORAGE_PREFIX = 'txn_test_params_'
const DIRECT_FORM_STORAGE_KEY = 'txn_test_direct_form'

const WAY_CODE_MAP = {
  wxpay: 'WX_JSAPI',
  alipay: 'ALI_JSAPI'
}

// 商户号直接下单模式下固定不可编辑的参数（按需求写死）
const FIXED_APP_ID = 'b8d4f722f4d24aeea353b72e'
const FIXED_CHANNEL_USER = 'o-cR83J9Ci0rRdxuNcMURxYKQJaU'
const FIXED_IF_CODE = 'yqt'

const SAMPLE_PARAMS = {
  wxpay: {
    appId: 'wx1ca4cb02bb26bcc4',
    timeStamp: '1780551122',
    nonceStr: '776e330ef8d647ae9cee371b103b3d49',
    package: 'prepay_id=wx0413320200852460b2ae36d983cb560001',
    signType: 'RSA',
    paySign: 'zVtuyLAApK4S0yLlMQ9YifonZtEan8O7teNketWJOniaXJLdEAMgp1WXERue6zeLFpn6bOnMkQ1fNv7kRYTw549ZePHCL+OXkEWJzvJsv18Yc0JjN52zNk5ZSasDr4jFkWjFE9eUSecuA1uWBfTtErig4W7EDI/b9pvkwHb5UlpxmfadA0L7OGYC5Dp6/bKNPgaZRAh59pwQJQToHYQwuG28buqm/UXjggFOM1hNiAZ3uAJsHbkFC/BbWyOrkIoaEvw71cK3e+PyFZlIK16ETdFUx8yvdYQea6CD7znxBB1HM1nQ0UJbS26ID9BqgMOVUZ7Mkgpfu1MBznIseZ2q8A=='
  },
  alipay: {
    tradeNO: '2026070922001234560000000000'
  },
  ysfpay: {
    redirectUrl: 'https://qr.95516.com/00010000/example-redirect-url'
  }
}

export default {
  name: 'TransactionTest',
  data() {
    return {
      payTypes: [
        { value: 'wxpay', label: '微信支付', color: '#07c160' },
        { value: 'alipay', label: '支付宝', color: '#1678ff' },
        { value: 'ysfpay', label: '云闪付', color: '#ff534d' }
      ],
      activePayType: 'wxpay',
      mode: 'manual',
      paramsText: {
        wxpay: '',
        alipay: '',
        ysfpay: ''
      },
      directForm: {
        mchNo: 'M1775020731',
        subAppId: '',
        amountYuan: '',
        subject: '',
        remark: ''
      },
      paying: false,
      resultMsg: '',
      resultSuccess: false,
      logs: []
    }
  },
  computed: {
    ua() {
      return navigator.userAgent
    },
    inWechat() {
      return /MicroMessenger/i.test(this.ua)
    },
    inAlipay() {
      return /AlipayClient/i.test(this.ua)
    },
    inUnionPay() {
      return /unionpay/i.test(this.ua)
    },
    envDetected() {
      return this.inWechat || this.inAlipay || this.inUnionPay
    },
    envText() {
      if (this.inWechat) return '微信内置浏览器（可测试微信支付）'
      if (this.inAlipay) return '支付宝内置浏览器（可测试支付宝支付）'
      if (this.inUnionPay) return '云闪付内置浏览器（可测试云闪付）'
      return '普通浏览器（JSBridge 调用将无法生效）'
    },
    currentColor() {
      const meta = this.payTypes.find(t => t.value === this.activePayType)
      return meta ? meta.color : '#07c160'
    },
    currentPlaceholder() {
      return JSON.stringify(SAMPLE_PARAMS[this.activePayType], null, 2)
    },
    currentTypeLabel() {
      const meta = this.payTypes.find(t => t.value === this.activePayType)
      return meta ? meta.label : this.activePayType
    },
    directWayCode() {
      return WAY_CODE_MAP[this.activePayType] || ''
    },
    FIXED_APP_ID() {
      return FIXED_APP_ID
    },
    FIXED_CHANNEL_USER() {
      return FIXED_CHANNEL_USER
    },
    FIXED_IF_CODE() {
      return FIXED_IF_CODE
    }
  },
  mounted() {
    this.loadAllParams()
    this.loadDirectForm()
    if (this.inAlipay) {
      this.activePayType = 'alipay'
    } else if (this.inUnionPay) {
      this.activePayType = 'ysfpay'
    } else {
      this.activePayType = 'wxpay'
    }
    this.addLog(`页面已加载，检测环境：${this.envText}`)
  },
  methods: {
    addLog(msg) {
      const now = new Date()
      const ts = `${now.getHours().toString().padStart(2, '0')}:${now.getMinutes().toString().padStart(2, '0')}:${now.getSeconds().toString().padStart(2, '0')}`
      this.logs.unshift(`[${ts}] ${msg}`)
    },
    loadAllParams() {
      Object.keys(this.paramsText).forEach(type => {
        const saved = localStorage.getItem(STORAGE_PREFIX + type)
        if (saved) this.paramsText[type] = saved
      })
    },
    saveCurrentParams() {
      localStorage.setItem(STORAGE_PREFIX + this.activePayType, this.paramsText[this.activePayType] || '')
    },
    switchType(type) {
      this.activePayType = type
      this.resultMsg = ''
    },
    switchMode(mode) {
      this.mode = mode
      this.resultMsg = ''
    },
    loadDirectForm() {
      try {
        const saved = JSON.parse(localStorage.getItem(DIRECT_FORM_STORAGE_KEY) || '{}')
        Object.assign(this.directForm, saved)
      } catch (e) {
        // 忽略历史脏数据
      }
    },
    saveDirectForm() {
      // 金额、标题、备注是每次测试都可能变化的内容，不持久化；appId/channelUser/ifCode 已写死，无需保存
      const { mchNo, subAppId } = this.directForm
      localStorage.setItem(DIRECT_FORM_STORAGE_KEY, JSON.stringify({ mchNo, subAppId }))
    },
    submitDirectPay() {
      this.resultMsg = ''
      if (!this.directWayCode) return

      const form = this.directForm
      if (!form.mchNo) {
        this.resultMsg = '请填写商户号'
        this.resultSuccess = false
        this.addLog('校验失败：商户号不能为空')
        return
      }
      const amountYuan = Number(form.amountYuan)
      if (!amountYuan || amountYuan <= 0) {
        this.resultMsg = '请填写正确的金额'
        this.resultSuccess = false
        this.addLog('校验失败：金额不合法 - ' + form.amountYuan)
        return
      }

      this.saveDirectForm()
      this.paying = true
      const amountFen = Math.round(amountYuan * 100)
      this.addLog(`开始调用 direct-pay：mchNo=${form.mchNo}, appId=${FIXED_APP_ID}, wayCode=${this.directWayCode}, amount=${amountFen}分`)

      directPay({
        mchNo: form.mchNo,
        appId: FIXED_APP_ID,
        subAppId: form.subAppId,
        wayCode: this.directWayCode,
        amount: amountFen,
        channelUser: FIXED_CHANNEL_USER,
        subject: form.subject,
        remark: form.remark,
        ifCode: FIXED_IF_CODE
      }).then(data => {
        this.addLog(`direct-pay 下单成功：${JSON.stringify(data)}`)
        this.handleDirectPayResult(data)
      }).catch(err => {
        this.paying = false
        const msg = (err && (err.message || err.msg)) || JSON.stringify(err)
        this.resultMsg = 'direct-pay 下单失败：' + msg
        this.resultSuccess = false
        this.addLog('direct-pay 下单失败：' + msg)
      })
    },
    handleDirectPayResult(data) {
      const payData = data && data.payData
      let parsed = payData
      if (typeof payData === 'string') {
        try {
          parsed = JSON.parse(payData)
        } catch (e) {
          parsed = payData
        }
      }

      if (this.activePayType === 'wxpay') {
        if (!parsed || typeof parsed !== 'object') {
          this.paying = false
          this.resultMsg = 'payData 不是合法的微信支付参数：' + JSON.stringify(payData)
          this.resultSuccess = false
          this.addLog('解析失败：payData 不是合法的微信支付参数')
          return
        }
        this.invokeWxpay(parsed)
      } else if (this.activePayType === 'alipay') {
        const tradeNO = (parsed && typeof parsed === 'object') ? (parsed.tradeNO || parsed.trade_no) : parsed
        this.invokeAlipay({ tradeNO })
      }
    },
    fillSample() {
      this.paramsText[this.activePayType] = JSON.stringify(SAMPLE_PARAMS[this.activePayType], null, 2)
      this.addLog(`已填充【${this.activePayType}】示例参数`)
    },
    formatParams() {
      try {
        const obj = JSON.parse(this.paramsText[this.activePayType] || '{}')
        this.paramsText[this.activePayType] = JSON.stringify(obj, null, 2)
      } catch (e) {
        this.addLog('格式化失败：JSON 格式不正确 - ' + e.message)
      }
    },
    clearParams() {
      this.paramsText[this.activePayType] = ''
    },
    parseCurrentParams() {
      const text = (this.paramsText[this.activePayType] || '').trim()
      if (!text) throw new Error('请先填写支付参数（可点击"填充示例参数"快速体验）')
      return JSON.parse(text)
    },
    triggerPay() {
      this.resultMsg = ''
      let params
      try {
        params = this.parseCurrentParams()
      } catch (e) {
        this.addLog('参数解析失败：' + e.message)
        this.resultMsg = '参数解析失败：' + e.message
        this.resultSuccess = false
        return
      }
      this.saveCurrentParams()
      this.paying = true
      this.addLog(`开始发起【${this.activePayType}】测试支付...`)

      if (this.activePayType === 'wxpay') {
        this.invokeWxpay(params)
      } else if (this.activePayType === 'alipay') {
        this.invokeAlipay(params)
      } else if (this.activePayType === 'ysfpay') {
        this.invokeYsfpay(params)
      }
    },
    invokeWxpay(params) {
      const onReady = () => {
        this.addLog('WeixinJSBridge 就绪，发起 getBrandWCPayRequest')
        // eslint-disable-next-line no-undef
        WeixinJSBridge.invoke('getBrandWCPayRequest', { ...params }, res => {
          this.paying = false
          this.addLog(`支付回调：${JSON.stringify(res)}`)
          if (res.err_msg === 'get_brand_wcpay_request:ok') {
            this.resultMsg = '支付成功！'
            this.resultSuccess = true
          } else if (res.err_msg === 'get_brand_wcpay_request:cancel') {
            this.resultMsg = '用户取消支付'
            this.resultSuccess = false
          } else {
            this.resultMsg = `支付失败：${res.err_msg || JSON.stringify(res)}`
            this.resultSuccess = false
          }
        })
      }
      if (typeof WeixinJSBridge === 'undefined') {
        this.addLog('WeixinJSBridge 未定义，等待 WeixinJSBridgeReady 事件')
        document.addEventListener('WeixinJSBridgeReady', onReady, false)
        setTimeout(() => {
          if (this.paying) {
            this.paying = false
            this.resultMsg = '等待超时：请在微信内置浏览器中打开本页面'
            this.resultSuccess = false
            this.addLog('超时：WeixinJSBridge 未就绪')
          }
        }, 5000)
      } else {
        onReady()
      }
    },
    invokeAlipay(params) {
      if (!params.tradeNO) {
        this.paying = false
        this.resultMsg = '参数缺失：tradeNO 不能为空'
        this.resultSuccess = false
        this.addLog('参数缺失：tradeNO 不能为空')
        return
      }
      const doPay = () => {
        this.addLog('AlipayJSBridge 就绪，发起 tradePay')
        // eslint-disable-next-line no-undef
        AlipayJSBridge.call('tradePay', { tradeNO: params.tradeNO }, data => {
          this.paying = false
          this.addLog(`支付回调：${JSON.stringify(data)}`)
          if (data.resultCode === '9000') {
            this.resultMsg = '支付成功！'
            this.resultSuccess = true
          } else if (data.resultCode === '6001') {
            this.resultMsg = '用户取消支付'
            this.resultSuccess = false
          } else {
            this.resultMsg = `支付结果：${JSON.stringify(data)}`
            this.resultSuccess = false
          }
        })
      }
      if (!window.AlipayJSBridge) {
        this.addLog('AlipayJSBridge 未定义，等待 AlipayJSBridgeReady 事件')
        document.addEventListener('AlipayJSBridgeReady', doPay, false)
        setTimeout(() => {
          if (this.paying) {
            this.paying = false
            this.resultMsg = '等待超时：请在支付宝内置浏览器中打开本页面'
            this.resultSuccess = false
            this.addLog('超时：AlipayJSBridge 未就绪')
          }
        }, 5000)
      } else {
        doPay()
      }
    },
    invokeYsfpay(params) {
      const url = params.redirectUrl || params.url
      if (!url) {
        this.paying = false
        this.resultMsg = '参数缺失：redirectUrl 不能为空'
        this.resultSuccess = false
        this.addLog('参数缺失：redirectUrl 不能为空')
        return
      }
      this.addLog(`即将跳转：${url}`)
      this.resultMsg = '正在跳转到支付页面...'
      this.resultSuccess = true
      setTimeout(() => {
        this.paying = false
        window.location.href = url
      }, 1000)
    }
  }
}
</script>

<style scoped>
.test-page {
  min-height: 100vh;
  background: #f5f5f5;
  padding: 0 0 40px;
  font-size: 14px;
  color: #333;
}

.test-header {
  background: linear-gradient(135deg, #556270, #4b5b6b);
  padding: 24px 16px 16px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.test-badge {
  background: #fff;
  color: #4b5b6b;
  font-size: 11px;
  font-weight: bold;
  padding: 2px 7px;
  border-radius: 4px;
  letter-spacing: 1px;
}

.test-title {
  color: #fff;
  font-size: 17px;
  font-weight: 600;
}

.card {
  background: #fff;
  margin: 12px 12px 0;
  border-radius: 10px;
  padding: 14px 16px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, .06);
}

.card-title {
  font-size: 13px;
  color: #999;
  margin-bottom: 10px;
  font-weight: 500;
  letter-spacing: .5px;
}

.info-row {
  display: flex;
  align-items: flex-start;
  padding: 5px 0;
  border-bottom: 1px solid #f5f5f5;
}

.info-row:last-child { border-bottom: none; }

.info-label {
  min-width: 80px;
  color: #666;
  font-size: 13px;
  padding-top: 1px;
}

.info-value {
  flex: 1;
  font-size: 13px;
  word-break: break-all;
  color: #333;
}

.info-value.small { font-size: 12px; color: #555; }
.info-value.breakable { word-break: break-all; }
.info-value.green { color: #07c160; }
.info-value.orange { color: #fa8c16; }

.mode-switch {
  display: flex;
  gap: 8px;
  margin: 14px 12px 0;
  background: #eaeaea;
  border-radius: 8px;
  padding: 3px;
}

.mode-item {
  flex: 1;
  text-align: center;
  padding: 8px 0;
  border-radius: 6px;
  color: #666;
  font-size: 13px;
  cursor: pointer;
}

.mode-item.active {
  background: #fff;
  color: #333;
  font-weight: 600;
  box-shadow: 0 1px 3px rgba(0, 0, 0, .1);
}

.tabs {
  display: flex;
  gap: 8px;
  margin: 14px 12px 0;
}

.tab-item {
  flex: 1;
  text-align: center;
  padding: 10px 0;
  border-radius: 8px;
  border: 1px solid #ddd;
  background: #fff;
  color: #666;
  font-size: 13px;
  cursor: pointer;
  transition: all .15s;
}

.tab-item.active {
  color: #fff;
  font-weight: 600;
}

.params-textarea {
  width: 100%;
  box-sizing: border-box;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 10px;
  font-size: 12px;
  font-family: 'Courier New', monospace;
  color: #333;
  resize: vertical;
  outline: none;
}

.params-textarea:focus { border-color: #999; }

.params-actions {
  display: flex;
  gap: 8px;
  margin-top: 10px;
}

.mini-btn {
  background: #f0f0f0;
  color: #555;
  border: none;
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
}

.mini-btn-clear { background: #fff2f0; color: #ff4d4f; }

.direct-unsupported {
  background: #fff7e6;
  color: #d48806;
  border: 1px solid #ffe58f;
  border-radius: 6px;
  padding: 10px 12px;
  font-size: 13px;
  line-height: 1.5;
}

.form-row {
  display: flex;
  align-items: center;
  padding: 7px 0;
  border-bottom: 1px solid #f5f5f5;
  gap: 10px;
}

.form-row:last-child { border-bottom: none; }

.form-label {
  min-width: 92px;
  font-size: 13px;
  color: #666;
  flex-shrink: 0;
}

.required { color: #ff4d4f; margin-left: 2px; }

.form-input {
  flex: 1;
  border: 1px solid #ddd;
  border-radius: 6px;
  padding: 7px 10px;
  font-size: 13px;
  outline: none;
  min-width: 0;
}

.form-input:focus { border-color: #999; }

.form-static {
  flex: 1;
  font-size: 13px;
  color: #333;
  font-family: 'Courier New', monospace;
  word-break: break-all;
}

.fixed-tag {
  display: inline-block;
  margin-left: 6px;
  background: #f0f0f0;
  color: #999;
  font-family: inherit;
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 4px;
  vertical-align: middle;
}

.pay-btn-wrap {
  margin: 20px 12px 0;
}

.pay-btn {
  width: 100%;
  color: #fff;
  border: none;
  border-radius: 10px;
  padding: 14px 0;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  letter-spacing: 1px;
}

.pay-btn:disabled { opacity: .5; cursor: not-allowed; }

.result-box {
  margin: 12px 12px 0;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
}

.result-box.success { background: #f6ffed; color: #52c41a; border: 1px solid #b7eb8f; }
.result-box.fail { background: #fff2f0; color: #ff4d4f; border: 1px solid #ffa39e; }

.log-card {
  background: #1e1e1e;
  margin: 12px 12px 0;
  border-radius: 10px;
  padding: 14px 16px;
}

.log-card .card-title { color: #888; }

.log-item {
  font-size: 11px;
  color: #a8ff78;
  font-family: 'Courier New', monospace;
  padding: 2px 0;
  border-bottom: 1px solid #2a2a2a;
  word-break: break-all;
}
</style>
